import { Injectable, inject } from '@angular/core';

import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ModalDialogService, ModalRef } from '@frontend/sports/modal/feature';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';

import { BetBuilderTrackingValues } from '../bet-builder-tracking';
import { BetBuilderHelpComponent } from '../components/help/bet-builder-help.component';
import { BetBuilderConfigService } from './bet-builder-config.service';

export interface BetBuilderHowToOptions {
    eventName: string;
    sportId: number;
    leagueId: number;
}

@Injectable({ providedIn: 'root' })
export class BetBuilderHowToDialogService {
    private dialogService = inject(ModalDialogService);
    private tracker = inject(TrackingService);
    private betBuilderConfigService = inject(BetBuilderConfigService);
    private sitecore = inject(Sitecore);

    private modalHandler: ModalRef | null = null;

    async openBuilder(options: BetBuilderHowToOptions): Promise<void> {
        if (!this.modalHandler) {
            this.trackOpenHowTo(options);
            this.modalHandler = this.dialogService.openDialog(BetBuilderHelpComponent, {
                data: {
                    sportId: options.sportId,
                    showHeader: false,
                    collapsible: false,
                    eventName: options.eventName,
                    leagueId: options.leagueId,
                    showHelpButtons: this.isEmbeddedUi,
                },
                settings: {
                    isModalDialog: true,
                    hideCloseButton: this.isEmbeddedUi,
                    showDialogHeader: true,
                    cssClass: this.isEmbeddedUi ? 'betbuilder-overlay-second-overlay embedded-help-page' : 'betbuilder-overlay-second-overlay',
                    title: this.sitecore.betBuilderHelpPage.messages?.helpPageTitle,
                },
            });

            this.modalHandler.result
                .then(
                    () => {},
                    () => this.trackCloseHowTo(options),
                )
                .finally(() => (this.modalHandler = null));
        }
    }

    private trackOpenHowTo(options: BetBuilderHowToOptions): void {
        this.tracker.track(trackingConstants.EVENT_TRACKING, {
            ...this.getTackingObject(options),
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetBuilderTrackingValues.Click,
        });
    }

    private trackCloseHowTo(options: BetBuilderHowToOptions): void {
        this.tracker.track(trackingConstants.EVENT_TRACKING, {
            ...this.getTackingObject(options),
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetBuilderTrackingValues.Close,
        });
    }

    private getTackingObject = (options: BetBuilderHowToOptions) => {
        return {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetBuilderTrackingValues.BetBuilder,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetBuilderTrackingValues.BuildaBetPod,
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: options.eventName,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: BetBuilderTrackingValues.HowToInfoPopup,
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
            [trackingConstants.SPORT_ID]: options.sportId,
            [trackingConstants.SPORT_LEAGUE_ID]: options.leagueId.toString(),
        };
    };

    get isEmbeddedUi(): boolean {
        return this.betBuilderConfigService.showEmbeddedUi();
    }
}
